import { type ReactNode, useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

import styles from './Tooltip.module.scss'

import { clearTimeoutIfExists, computePortaledComponentPosition, type Placement } from '@/utils'

export interface TooltipProps {
  placement: Placement
  text: string | ReactNode
  textClass?: 'normal' | 'status'
  children: ReactNode
  open?: boolean
  delay?: number // Delay in milliseconds before showing tooltip on hover
  onHover?: () => void // Callback when tooltip is hovered
  offsetFromTarget?: number // Distance from tooltip to target
  offsetAlongEdge?: number // Distance from tooltip to target along the edge
  hideArrow?: boolean
  style?: React.CSSProperties
  onClick?: () => void
}

const Tooltip = (props: TooltipProps) => {
  const {
    placement,
    text,
    textClass,
    children,
    open,
    delay,
    onHover,
    offsetFromTarget,
    offsetAlongEdge,
    hideArrow,
    style,
  } = props
  const tooltipRef = useRef<HTMLDivElement>(null)
  const targetRef = useRef<HTMLDivElement>(null)
  const [position, setPosition] = useState({ top: 0, left: 0 })
  const [isHovered, setIsHovered] = useState(false)
  const [showTooltip, setShowTooltip] = useState(false)
  const [textClassState, setTextClassState] = useState(styles.normalText)

  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const closeTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const resizeObserverRef = useRef<ResizeObserver | null>(null)

  useEffect(() => {
    // Reset hover state when open prop changes
    setIsHovered(!!open)
  }, [open])

  const handleAnchorMouseEnter = () => {
    if (open !== undefined) return

    clearTimeoutIfExists(closeTimeoutRef.current)
    hoverTimeoutRef.current = setTimeout(() => {
      setIsHovered(true)
      onHover?.()
    }, delay || 500)
  }

  const handleAnchorMouseLeave = () => {
    if (open !== undefined) return

    clearTimeoutIfExists(hoverTimeoutRef.current)
    closeTimeoutRef.current = setTimeout(() => {
      if (!isTooltipHovered()) {
        setIsHovered(false)
      }
    }, 200)
  }

  const handleTooltipMouseEnter = () => {
    clearTimeoutIfExists(closeTimeoutRef.current)
    setIsHovered(true)
  }

  const handleTooltipMouseLeave = () => {
    setIsHovered(false)
  }

  const isTooltipHovered = useCallback(() => {
    return tooltipRef.current ? tooltipRef.current.matches(':hover') : false
  }, [])

  useEffect(() => {
    switch (textClass) {
      case 'normal':
        setTextClassState(styles.normalText)
        break
      case 'status':
        setTextClassState(styles.statusText)
        break
    }
  }, [textClass])

  useEffect(() => {
    const shouldShow = open !== undefined ? open : isHovered
    setShowTooltip(shouldShow)
  }, [open, isHovered])

  const updatePosition = useCallback(() => {
    requestAnimationFrame(() => {
      // Wait for the browser to update the layout for accurate measurements
      const target = targetRef.current
      const tooltip = tooltipRef.current
      const arrowWidth = 10
      const offsetFromTargetState = offsetFromTarget !== undefined ? offsetFromTarget : 5
      const totalOffset = hideArrow ? offsetFromTargetState : offsetFromTargetState + arrowWidth // Distance from tooltip to target, including arrow
      const arrowFromEdge = 12 + (offsetAlongEdge || 0) // Distance from arrow to edge of tooltip

      const position = computePortaledComponentPosition(target, tooltip, placement, totalOffset, arrowFromEdge)
      setPosition(position)
    })
  }, [placement, offsetFromTarget, hideArrow, offsetAlongEdge])

  useEffect(() => {
    if (tooltipRef.current && showTooltip) {
      updatePosition()

      // When the user changes monitors, DPI can change, causing the tooltip to be resized and the computed position to be off
      // The resize observer will update the position when the tooltip is resized
      resizeObserverRef.current = new ResizeObserver(() => updatePosition())
      resizeObserverRef.current.observe(tooltipRef.current)

      return () => resizeObserverRef.current?.disconnect()
    }
  }, [showTooltip, updatePosition])

  if (!text) {
    return children
  }

  return (
    <>
      <div ref={targetRef} onMouseEnter={handleAnchorMouseEnter} onMouseLeave={handleAnchorMouseLeave}>
        {children}
      </div>
      {createPortal(
        <div
          ref={tooltipRef}
          className={`${styles.tooltip} ${styles[placement]} ${textClassState} ${showTooltip ? styles.visible : ''} ${hideArrow ? '' : styles.showArrow}`}
          style={{
            ...style,
            top: `${position.top}px`,
            left: `${position.left}px`,
          }}
          onMouseEnter={handleTooltipMouseEnter}
          onMouseLeave={handleTooltipMouseLeave}
          onClick={props.onClick}
        >
          {text}
        </div>,
        document.body
      )}
    </>
  )
}

export default Tooltip
